<template>
  <div class="agent-tool" :class="{ 'agent-tool--error': message.toolIsError, 'agent-tool--pending': isPending }">
    <button type="button" class="agent-tool__summary" :aria-expanded="expanded" @click="expanded = !expanded">
      <span class="agent-tool__caret" :class="{ 'agent-tool__caret--open': expanded }" v-html="caretIcon" />
      <span class="agent-tool__label">{{ label }}</span>
      <span v-if="!isPending && !hasResult" class="agent-tool__waiting">working…</span>
    </button>

    <div v-if="expanded" class="agent-tool__detail">
      <div class="agent-tool__section">
        <div class="agent-tool__section-title">{{ codeInput ? 'Script' : 'Input' }}</div>
        <pre class="agent-tool__pre">{{ codeInput ?? prettyInput }}</pre>
      </div>
      <div v-if="hasResult" class="agent-tool__section">
        <div class="agent-tool__section-title">{{ message.toolIsError ? 'Error' : 'Result' }}</div>
        <pre class="agent-tool__pre">{{ message.toolResultText }}</pre>
      </div>
    </div>

    <div v-if="isPending" class="agent-tool__approval">
      <div class="agent-tool__approval-text">
        <strong>The agent wants to change your data.</strong>
        Check the script above, then decide.
      </div>
      <div class="agent-tool__approval-actions">
        <fs-button size="small" secondary :disabled="answering" @click="answer(false)">Deny</fs-button>
        <fs-button size="small" accent :disabled="answering" @click="answer(true)">Allow</fs-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { AgentApprovalState, type AgentMessageDto } from '@/ApiClient';
import { useAgentChat } from '@/store/agent-chat';
import { toolLabel } from '@lib/agent-tools';
import caretIcon from '@icons/other/caret-down.svg';
import FsButton from '@components/buttons/fs-button.vue';

const { message } = defineProps<{ message: AgentMessageDto }>();

const expanded = ref(false);
const answering = ref(false);

const isPending = computed(() => message.approvalState === AgentApprovalState.Pending);
const hasResult = computed(() => message.toolResultText != null);

const label = computed(() => toolLabel(message.toolName, message.toolServer, message.approvalState));

const input = computed<Record<string, unknown>>(() => {
  try {
    return JSON.parse(message.toolInputJson ?? '{}');
  } catch {
    return {};
  }
});
// A script is what the user wants to read; the rest of the arguments are noise next to it.
const codeInput = computed(() => (typeof input.value.code === 'string' ? input.value.code : null));
const prettyInput = computed(() => JSON.stringify(input.value, null, 2));

const answer = async (allow: boolean) => {
  answering.value = true;
  try {
    await useAgentChat().approve(message.id, allow);
  } finally {
    answering.value = false;
  }
};
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.agent-tool {
  border: 1px solid $c-hairline;
  border-radius: $br-medium-heavy;
  background: $c-canvas;
  font-size: 1.3rem;

  &--error {
    border-color: rgba($c-red, 0.4);
  }

  &--pending {
    border-color: $c-accent-deep;
    background: $c-accent-wash;
  }

  &__summary {
    display: flex;
    align-items: baseline;
    gap: 0.8rem;
    width: 100%;
    padding: 0.8rem 1.2rem;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font: inherit;
    color: $c-ink;
  }

  &__caret {
    display: inline-flex;
    flex: none;
    align-self: center;
    transition: transform $transition-fast ease;
    transform: rotate(-90deg);

    :deep(svg) {
      width: 1rem;
      height: 1rem;
      fill: rgba($c-ink, 0.5);
    }

    &--open {
      transform: rotate(0);
    }
  }

  &__label {
    flex: none;
    font-weight: 600;
  }

  &__waiting {
    color: $c-ink-soft;
    font-style: italic;
  }

  &__detail {
    padding: 0 1.2rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  &__section-title {
    margin-bottom: 0.3rem;
    font-family: 'Montserrat', sans-serif;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba($c-ink, 0.5);
  }

  &__pre {
    margin: 0;
    max-height: 32rem;
    padding: 0.8rem 1rem;
    border-radius: $br-medium;
    background: $c-surface;
    font-size: 1.2rem;
    line-height: 1.7rem;
    white-space: pre-wrap;
    overflow: auto;
  }

  &__approval {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    padding: 1rem 1.2rem;
    border-top: 1px solid $c-hairline;
  }

  &__approval-text {
    flex: 1;
    color: $c-ink-body;

    strong {
      display: block;
      color: $c-ink;
    }
  }

  &__approval-actions {
    display: flex;
    gap: 0.8rem;
  }
}
</style>
